import { UserGroupMember } from '../models/user-group-member.model';
import { UserGroup } from '../models/user-group.model';
import { AuthService } from './auth.service';
import { HttpClient } from '@angular/common/http';
import { Globals } from '../shared/globals';
import { JsonSerializer } from 'typescript-json-serializer';
import { UserGroupMembershipStatus } from '../models/user-group-membership-status.model';
import {Injectable} from "@angular/core";

/**
 * Service responsible for managing user groups.
 *
 * @export
 * @class UserGroupService
 */
@Injectable()
export class UserGroupService {
  private jsonSerializer: JsonSerializer;
  constructor(
    private authService: AuthService,
    private http: HttpClient,
    private globals: Globals
  ) {
    this.jsonSerializer = new JsonSerializer();
  }

  /**
   * Fetches list of user groups where the user belongs to.
   *
   * @returns {Promise<UserGroup[]>}
   * @memberof UserGroupService
   */
  public getUserGroups(): Promise<UserGroup[]> {
    return this.authService.getToken().then((token) => {
      return this.performGetUserGroupsRequest(token);
    });
  }

  /**
   * Fetches group details by its identifier.
   *
   * @param {string} id
   * @returns {Promise<UserGroup>}
   * @memberof UserGroupService
   */
  public getUserGroup(id: string): Promise<UserGroup> {
    return this.authService.getToken().then((token) => {
      return this.performGetUserGroupRequest(token, id);
    });
  }

  /**
   * Creates user group with given name and description.
   *
   * @param {string} name
   * @param {string} description
   * @returns {Promise<UserGroup>}
   * @memberof UserGroupService
   */
  public createUserGroup(
    name: string,
    description: string
  ): Promise<UserGroup> {
    return this.authService.getToken().then((token) => {
      return this.performCreateUserGroupRequest(token, name, description);
    });
  }

  /**
   * Updates user group information.
   *
   * @param {UserGroup} group
   * @returns {Promise<UserGroup>}
   * @memberof UserGroupService
   */
  public updateUserGroup(group: UserGroup): Promise<UserGroup> {
    return this.authService.getToken().then((token) => {
      return this.performUpdateUserGroupRequest(
        token,
        group.id,
        group.name,
        group.description
      );
    });
  }

  /**
   * Deletes user group.
   *
   * @param {UserGroup} group
   * @returns {Promise<void>}
   * @memberof UserGroupService
   */
  public deleteUserGroup(group: UserGroup): Promise<void> {
    return this.authService.getToken().then((token) => {
      return this.performDeleteUserGroupRequest(token, group.id);
    });
  }

  /**
   * Removes user from given group.
   *
   * @param {UserGroupMember} user
   * @param {UserGroup} group
   * @returns {Promise<void>}
   * @memberof UserGroupService
   */
  public removeUserFromGroup(
    user: UserGroupMember,
    group: UserGroup
  ): Promise<void> {
    return this.authService.getToken().then((token) => {
      return this.performRemoveUserFromGroupRequest(token, user.id, group.id);
    });
  }

  /**
   * Removes currently authenticated user from given group.
   *
   * @param {UserGroup} group
   * @returns {Promise<void>}
   * @memberof UserGroupService
   */
  public leaveUserGroupRequest(group: UserGroup): Promise<void> {
    return this.authService.getToken().then((token) => {
      return this.performLeaveUserGroupRequest(token, group.id);
    });
  }

  /**
   * Adds users with a given email addresses to the group.
   *
   * @param {string[]} userEmails
   * @param {string} email-address
   * @param {UserGroup} group
   * @returns {Promise<void>}
   * @memberof UserGroupService
   */
  public addUsersToGroup(
    userEmails: string[],
    group: UserGroup
  ): Promise<void> {
    return this.authService.getToken().then((token) => {
      return this.performAddUsersToGroupRequest(token, userEmails, group.id);
    });
  }

  public fetchUserGroupMembershipStatus(
    userEmails: string[],
    group: UserGroup
  ): Promise<UserGroupMembershipStatus[]> {
    return this.authService.getToken().then((token) => {
      return this.performGetUserGroupMembershipStatus(
        token,
        userEmails,
        group.id
      );
    });
  }

  /**
   * Removes admin privileges from given group member.
   *
   * @param {UserGroupMember} user
   * @param {UserGroup} group
   * @returns {Promise<void>}
   * @memberof UserGroupService
   */
  public removeUserFromGroupAdmins(
    user: UserGroupMember,
    group: UserGroup
  ): Promise<void> {
    return this.authService.getToken().then((token) => {
      return this.performRemoveUserFromGroupAdminsRequest(
        token,
        user.id,
        group.id
      );
    });
  }

  /**
   * Grants admin privileges to given group member.
   *
   * @param {UserGroupMember} user
   * @param {UserGroup} group
   * @returns {Promise<void>}
   * @memberof UserGroupService
   */
  public grantGroupAdminPrivilegesToUser(
    user: UserGroupMember,
    group: UserGroup
  ): Promise<void> {
    return this.authService.getToken().then((token) => {
      return this.performAddUserToGroupAdminsRequest(token, user.id, group.id);
    });
  }

  // Internal methods

  private performGetUserGroupsRequest(token: string): Promise<UserGroup[]> {
    const url = new URL(this.globals.apiUrl(`usergroups`));

    return new Promise((resolve, reject) => {
      this.http
        .get(url.toString(), { headers: this.authService.headers(token) })
        .toPromise()
        .then((response) => {
          const values = Object.keys(response).map((item) =>
            this.jsonSerializer.deserializeObject<UserGroup>(response[item], UserGroup)
          );
          resolve(values);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  private performGetUserGroupRequest(
    token: string,
    id: string
  ): Promise<UserGroup> {
    const url = new URL(this.globals.apiUrl(`usergroups`) + '/' + id);

    return new Promise((resolve, reject) => {
      this.http
        .get(url.toString(), { headers: this.authService.headers(token) })
        .toPromise()
        .then((response) => {
          const object = this.jsonSerializer.deserializeObject<UserGroup>(response, UserGroup);
          resolve(object);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  private performCreateUserGroupRequest(
    token: string,
    name: string,
    description: string
  ): Promise<UserGroup> {
    const payload = {
      Name: name,
      Description: description,
    };

    const url = new URL(this.globals.apiUrl('usergroups'));
    return new Promise((resolve, reject) => {
      this.http
        .post(url.toString(), payload, {
          headers: this.authService.headers(token),
        })
        .toPromise()
        .then((response) => {
          const object = this.jsonSerializer.deserializeObject<UserGroup>(response, UserGroup);
          resolve(object);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  private performUpdateUserGroupRequest(
    token: string,
    id: string,
    name: string,
    description: string
  ): Promise<UserGroup> {
    const payload = {
      Id: id,
      Name: name,
      Description: description,
    };

    const url = new URL(this.globals.apiUrl('usergroups/'));
    return new Promise((resolve, reject) => {
      this.http
        .put(url.toString(), payload, {
          headers: this.authService.headers(token),
        })
        .toPromise()
        .then((response) => {
          const object = this.jsonSerializer.deserializeObject<UserGroup>(response, UserGroup);
          resolve(object);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  private performDeleteUserGroupRequest(
    token: string,
    groupId: string
  ): Promise<void> {
    const url = new URL(this.globals.apiUrl(`usergroups`) + '/' + groupId);

    return new Promise((resolve, reject) => {
      this.http
        .delete(url.toString(), { headers: this.authService.headers(token) })
        .toPromise()
        .then((_) => {
          resolve();
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  // TODO: Deprecate this method. Use the bulk add instead and pass just one email.
  private performAddUserToGroupRequest(
    token: string,
    userEmail: string,
    groupId: string
  ): Promise<void> {
    const params = new URLSearchParams({ email: userEmail });
    const url = new URL(
      this.globals.apiUrl(`usergroups`) +
      '/' +
      groupId +
      '/users?' +
      params.toString()
    );

    return new Promise((resolve, reject) => {
      this.http
        .post(url.toString(), null, {
          headers: this.authService.headers(token),
        })
        .toPromise()
        .then((_) => {
          resolve();
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  private performAddUsersToGroupRequest(
    token: string,
    userEmails: string[],
    groupId: string
  ): Promise<void> {
    const payload = userEmails;

    const url = new URL(
      this.globals.apiUrl(`usergroups`) +
      '/' +
      groupId +
      '/users'
    );

    return new Promise((resolve, reject) => {
      this.http
        .post(url.toString(), payload, {
          headers: this.authService.headers(token),
        })
        .toPromise()
        .then((_) => {
          resolve();
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  private performGetUserGroupMembershipStatus(
    token: string,
    userEmails: string[],
    groupId: string
  ): Promise<UserGroupMembershipStatus[]> {
    const payload = userEmails;

    const url = new URL(
      this.globals.apiUrl(`usergroups`) +
      '/' +
      groupId +
      '/membership-status'
    );

    return new Promise((resolve, reject) => {
      this.http
        .post(url.toString(), payload, {
          headers: this.authService.headers(token),
        })
        .toPromise()
        .then((response) => {
          const values = Object.keys(response).map((item) =>
            this.jsonSerializer.deserializeObject<UserGroupMembershipStatus>(
              response[item],
              UserGroupMembershipStatus
            )
          );
          resolve(values);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  private performRemoveUserFromGroupRequest(
    token: string,
    userId: string,
    groupId: string
  ): Promise<void> {
    const url = new URL(
      this.globals.apiUrl(`usergroups`) + '/' + groupId + '/users/' + userId
    );

    return new Promise((resolve, reject) => {
      this.http
        .delete(url.toString(), { headers: this.authService.headers(token) })
        .toPromise()
        .then((_) => {
          resolve();
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  private performLeaveUserGroupRequest(
    token: string,
    groupId: string
  ): Promise<void> {
    const url = new URL(
      this.globals.apiUrl(`usergroups`) + '/' + groupId + '/leave'
    );

    return new Promise((resolve, reject) => {
      this.http
        .post(url.toString(), null, {
          headers: this.authService.headers(token),
        })
        .toPromise()
        .then((_) => {
          resolve();
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  private performAddUserToGroupAdminsRequest(
    token: string,
    userId: string,
    groupId: string
  ): Promise<void> {
    const params = new URLSearchParams({ userid: userId });
    const url = new URL(
      this.globals.apiUrl(`usergroups`) +
      '/' +
      groupId +
      '/admins?' +
      params.toString()
    );

    return new Promise((resolve, reject) => {
      this.http
        .post(url.toString(), null, {
          headers: this.authService.headers(token),
        })
        .toPromise()
        .then((_) => {
          resolve();
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  private performRemoveUserFromGroupAdminsRequest(
    token: string,
    userId: string,
    groupId: string
  ): Promise<void> {
    const url = new URL(
      this.globals.apiUrl(`usergroups`) + '/' + groupId + '/admins/' + userId
    );

    return new Promise((resolve, reject) => {
      this.http
        .delete(url.toString(), { headers: this.authService.headers(token) })
        .toPromise()
        .then((_) => {
          resolve();
        })
        .catch((error) => {
          reject(error);
        });
    });
  }
}
